// packages/web/src/components/AdminLoginPage.tsx
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ShieldCheck, Lock, Mail, AlertCircle, Sparkles, Eye, EyeOff, ArrowLeft } from 'lucide-react'
import { useNavigate, useLocation } from '@tanstack/react-router'
import { AdminLoginInput } from '@buddys-choice/shared'
import type { AdminLoginInput as AdminLoginInputType } from '@buddys-choice/shared'
import { apiPost } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import type { User } from '../context/AuthContext'
import { useSiteConfig } from '../context/SiteConfigContext'

export const AdminLoginPage: React.FC = () => {
  const { login } = useAuth()
  const { config } = useSiteConfig()
  const navigate = useNavigate()
  const location = useLocation()
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AdminLoginInputType>({
    resolver: zodResolver(AdminLoginInput),
    defaultValues: { email: '', password: '' },
  })

  const redirectTo = (() => {
    const search = (location.search ?? {}) as Record<string, unknown>
    return typeof search.redirect === 'string' && search.redirect.startsWith('/')
      ? search.redirect
      : '/admin/overview'
  })()

  const handleLogin = async (data: AdminLoginInputType) => {
    setError('')
    try {
      const res = await apiPost<{ token: string; user: User }>('auth/admin/login', data)
      login(res.token, res.user)
      navigate({ to: redirectTo, replace: true })
    } catch (err: unknown) {
      setError((err as Error).message || 'Invalid admin credentials. Access denied.')
    }
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12 bg-[var(--bg-main)] overflow-hidden font-sans">
      {/* Dynamic Luxury Ambient Glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[var(--gold-main)]/10 blur-[160px] pointer-events-none rounded-full" />
      <div className="absolute bottom-10 right-10 w-[400px] h-[400px] bg-[var(--gold-deep)]/10 blur-[140px] pointer-events-none rounded-full" />

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />

      <div className="w-full max-w-5xl relative z-10 grid lg:grid-cols-12 gap-8 items-center">
        {/* Left Side: Brand Identity Banner */}
        <div className="lg:col-span-6 flex flex-col justify-center items-center lg:items-start text-center lg:text-left space-y-6 p-4 sm:p-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--gold-main)]/10 border border-[var(--gold-main)]/30 text-xs font-semibold text-[var(--gold-main)] backdrop-blur-md">
            <Sparkles className="w-3.5 h-3.5" />
            <span>OFFICIAL ADMIN PORTAL</span>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-center lg:justify-start gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-[var(--gold-deep)] via-[var(--gold-main)] to-[var(--gold-light)] p-0.5 shadow-[0_0_30px_rgba(212,175,55,0.4)]">
                <div className="w-full h-full rounded-full bg-[var(--bg-main)] flex items-center justify-center">
                  <span className="font-serif font-black text-xl gold-shimmer-text">
                    {config.brand_name ? config.brand_name.charAt(0) : 'B'}
                  </span>
                </div>
              </div>
              <h1 className="text-3xl sm:text-4xl font-title font-extrabold gold-shimmer-text uppercase tracking-wide">
                {config.brand_name}
              </h1>
            </div>
            <p className="text-sm text-[var(--text-muted)] font-light leading-relaxed max-w-md">
              Secure Management Console for Catalogue, Stock, Orders &amp; Sales.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 w-full max-w-md text-left pt-2">
            <div className="p-3.5 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border)] backdrop-blur-md">
              <div className="text-[11px] font-bold text-[var(--gold-main)] uppercase tracking-wider">Restricted Area</div>
              <div className="text-xs text-[var(--text-primary)] font-medium mt-0.5">Role-Based Access</div>
            </div>
            <div className="p-3.5 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border)] backdrop-blur-md">
              <div className="text-[11px] font-bold text-[var(--gold-main)] uppercase tracking-wider">Audit Security</div>
              <div className="text-xs text-[var(--text-primary)] font-medium mt-0.5">JWT Session Encryption</div>
            </div>
          </div>
        </div>

        {/* Right Side: Login Form Card */}
        <div className="lg:col-span-6">
          <div className="glass-luxury-card p-6 sm:p-10 border-[var(--gold-main)]/30 shadow-[0_10px_50px_rgba(0,0,0,0.5)] relative overflow-hidden backdrop-blur-2xl">
            {/* Top Accent Line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[var(--gold-dark)] via-[var(--gold-main)] to-[var(--gold-light)]" />

            <div className="text-center mb-8 space-y-2">
              <div className="w-14 h-14 rounded-2xl bg-[var(--gold-main)]/15 border border-[var(--gold-main)]/40 flex items-center justify-center text-[var(--gold-main)] mx-auto shadow-inner">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <h2 className="text-2xl font-title font-extrabold text-[var(--text-primary)] tracking-tight">
                Portal Authentication
              </h2>
              <p className="text-xs text-[var(--text-muted)]">
                Please enter your administrator credentials to continue.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-xs text-red-300 flex items-start gap-3 animate-fadeIn">
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit(handleLogin)} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                  Admin Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    {...register('email')}
                    placeholder="admin@buddyschoice.com"
                    autoComplete="email"
                    aria-invalid={!!errors.email}
                    className="w-full bg-[var(--bg-main)]/90 border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-3 pl-10 pr-4 text-xs text-[var(--text-primary)] placeholder-[var(--text-faint)] transition outline-none"
                  />
                  <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-[var(--gold-main)]" />
                </div>
                {errors.email && (
                  <span className="text-[11px] text-red-400 mt-1 block">{errors.email.message}</span>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password')}
                    placeholder="••••••••••••"
                    autoComplete="current-password"
                    aria-invalid={!!errors.password}
                    className="w-full bg-[var(--bg-main)]/90 border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-3 pl-10 pr-10 text-xs text-[var(--text-primary)] placeholder-[var(--text-faint)] transition outline-none"
                  />
                  <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-[var(--gold-main)]" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-3.5 text-[var(--text-faint)] hover:text-[var(--text-primary)] transition"
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && (
                  <span className="text-[11px] text-red-400 mt-1 block">{errors.password.message}</span>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-gold py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider shadow-xl flex items-center justify-center gap-2 group transition"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 group-hover:scale-110 transition" />
                    <span>Authenticate &amp; Access Dashboard</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-[var(--border)] text-center">
              <button
                onClick={() => navigate({ to: '/' })}
                className="inline-flex items-center gap-2 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)] transition font-medium"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Return to Storefront</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
